import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  Subject,
  Subscription,
  combineLatest,
  filter,
  of as just,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { SessionRepository } from '@app-core/state/session.repository';
import { makeObjectWithNoNullValues } from '@app-shared/functions/helpers.functions';
import { emitOnce } from '@ngneat/elf';

import { NotificationsResponse } from '../interfaces/notifications-response.interface';
import { NotificationsRepository } from './notification.repository';
import { CountryRepository } from '../../countries/state/country.repository';
import { CountriesFilterRepository } from '../../filters/countries/state/countries-filter.repository';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly http = inject(HttpClient);
  private readonly notificationsRepo = inject(NotificationsRepository);
  private readonly sessionRepo = inject(SessionRepository);
  private readonly countryRepo = inject(CountryRepository);
  private readonly countriesFilterRepo = inject(CountriesFilterRepository);

  private notificationsSubscription: Subscription | undefined = undefined;
  private cancelStream = new Subject<void>();
  cancelRequest = new Subject<void>();

  init(): void {
    combineLatest([
      this.sessionRepo.isLoggedIn$,
      this.countryRepo.activeCountryId$,
      this.countriesFilterRepo.selectedCountries$,
    ]).subscribe(([isLoggedIn, _activeCountry]) => {
      if (isLoggedIn) {
        this.notificationsRepo.resetStore();
        this.initNotificationsSubscription();
      } else {
        this.destroyNotificationsSubscription();
        this.notificationsRepo.resetStore();
        this.cancelStream.next();
      }
    });
  }

  private handleResponseData(data: NotificationsResponse): void {
    if (
      data.pages.current === 1 &&
      (!this.notificationsRepo.getStoreProp('wasDialogOpened') ||
        this.notificationsRepo.getStoreProp('mode') !== 'unread')
    ) {
      this.notificationsRepo.setNotifications(data.data);
    } else {
      this.notificationsRepo.upsertNotifications(data.data);
    }

    if (this.notificationsRepo.getStoreProp('wasDialogOpened')) {
      setTimeout(() => {
        this.notificationsRepo.updateNotificationsReadStatus(
          data.data.map(n => n.id),
          true,
        );
      }, 2000);
    }

    this.notificationsRepo.updateState({
      currentPage: data.pages.current,
      totalPages: data.pages.total,
      totalItems: data.total,
      nextPage: data.pages.next,
      numberOfUnread: data.aggregations?.unread_notifications || 0,
    });
  }

  private getNotifications(): Observable<NotificationsResponse | null> {
    this.notificationsRepo.setLoading(true);
    this.cancelRequest.next();

    if (!this.notificationsRepo.getStoreProp('currentPage')) {
      this.notificationsRepo.setLoading(false);
      this.notificationsRepo.setNotifications([]);

      return just(null);
    }

    // First check if we have an active country
    return this.countryRepo.activeCountryId$.pipe(
      take(1),
      filter(countryId => !!countryId),
      switchMap(countryId => {
        const mode = this.notificationsRepo.getStoreProp('mode');
        const apiPath =
          mode === 'unread'
            ? '/product/notifications-unread'
            : '/product/notifications';

        const params = makeObjectWithNoNullValues({
          read: this.notificationsRepo.getStoreProp('wasDialogOpened')
            ? '1'
            : null,
          page: this.notificationsRepo.getStoreProp('currentPage').toString(),
          country: countryId.toString(),
        });

        return this.http
          .get<NotificationsResponse>(`${environment.apiUrl}${apiPath}`, {
            params,
          })
          .pipe(
            tap(data => {
              emitOnce(() => {
                this.handleResponseData(data);
                this.notificationsRepo.setLoading(false);
              });
            }),
            takeUntil(this.cancelRequest),
          );
      }),
    );
  }

  private handleHttpError(err: HttpErrorResponse): void {
    this.notificationsRepo.setLoading(false);

    if (err.status === 404 && err.error && err.error.data && err.error.pages) {
      const dataRes: NotificationsResponse = err.error;
      this.notificationsRepo.updateState({
        currentPage: dataRes.pages.current,
        totalPages: dataRes.pages.total,
        totalItems: dataRes.total,
        nextPage: dataRes.pages.next,
        numberOfUnread: dataRes.aggregations?.unread_notifications || 0,
      });
    } else {
      this.notificationsRepo.setError(err);
    }
  }

  initNotificationsSubscription(): void {
    this.notificationsSubscription = combineLatest([
      this.notificationsRepo.currentPage$,
      this.notificationsRepo.mode$,
    ]).subscribe({
      next: () => {
        // eslint-disable-next-line rxjs/no-nested-subscribe
        this.getNotifications().subscribe({
          error: (err: HttpErrorResponse) => this.handleHttpError(err),
        });
      },
    });
  }

  destroyNotificationsSubscription(): void {
    this.cancelRequest.next();
    this.notificationsSubscription?.unsubscribe();
  }

  markNotificationsAsRead(
    ids: number[],
  ): Observable<{ unread_notifications: number } | null> {
    if (!ids?.length) return just(null);

    const unreadNotifications = Object.values(
      this.notificationsRepo.getState().entities,
    ).filter(entity => ids.includes(entity.id!) && !entity.read);

    if (!unreadNotifications.length) return just(null);

    return this.http
      .post<{
        unread_notifications: number;
      }>(`${environment.apiUrl}/product/notifications-read`, {
        notifications: unreadNotifications.map(n => n.id),
      })
      .pipe(
        tap(data => {
          this.notificationsRepo.setUnreadNumber(data.unread_notifications);
          this.notificationsRepo.updateNotificationsReadStatus(ids, true);
        }),
      );
  }

  removeReadNotificationsFromStore(): void {
    const readNotifications = Object.values(
      this.notificationsRepo.getState().entities,
    )
      .filter(n => !!n.read)
      .map(n => n.id);

    if (readNotifications.length) {
      this.notificationsRepo.removeNotifications(readNotifications);
    }
  }
}
